/**
 * Where the composer draws itself (RFC 0266 §3e).
 *
 * ONE composer instance, two representations. Legacy grew a second component
 * (ThreadContainerCompact) for the collapsed-panel case, which meant two drafts,
 * two model selections, and two send paths: a half-typed message evaporated the
 * moment you collapsed the panel.
 *
 * Instead the shell publishes a slot. The Composer stays mounted at its stable
 * position in the React tree (inside ThreadView, inside a hidden AgentPanel) and
 * PORTALS itself into the slot when the shell offers one. React preserves the
 * component's state across the move because its tree position never changed —
 * only the DOM node relocates.
 *
 * The agent panel is hidden with `display:none` rather than unmounted for
 * exactly this reason: unmounting would take the draft with it.
 */

import { createContext, useContext } from 'react'

/**
 * `page` is the composer as the SUBJECT of a page rather than a bar at the foot
 * of one (RFC 0284). The new-pane page is a box in the middle of empty space,
 * and a box that size wants type and padding to match; at the thread's scale it
 * reads as a toolbar that lost its thread. Same component, same state, same
 * controls — a third representation, which is what this slot exists to allow.
 */
export type ComposerVariant = 'panel' | 'compact' | 'page'

export interface ComposerSlot {
  /**
   * How big, and nothing else.
   *
   * `compact` used to mean "portalled somewhere", which conflated the scale
   * with the destination and left the composer strip (RFC 0284 S8d) unable to
   * ask for the tight floating box it is: it portals nowhere, so it was handed
   * the thread's scale instead. The two are separate questions now, and
   * `element` answers the other one.
   */
  variant: ComposerVariant
  /** Where to draw it. Null draws in place, which is what a host that has
   *  nowhere to send it passes. Any variant may portal or not. */
  element: HTMLElement | null
}

const ComposerSlotContext = createContext<ComposerSlot>({
  variant: 'panel',
  element: null,
})

export const ComposerSlotProvider = ComposerSlotContext.Provider

export function useComposerSlot(): ComposerSlot {
  return useContext(ComposerSlotContext)
}
